export type ApiErrorInfo = {
  status?: number;
  code?: string;
  message?: string;
  url?: string;
};

const MFA_ERROR_MESSAGES = new Set([
  'INVALID_MFA_OTP_TOKEN',
  'INVALID_MFA_OTP_TOKEN_OPERATOR_MISMATCH',
  'INVALID_YOTP_TOKEN',
  'MISSING_MFA_OTP_TOKEN'
]);

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function parsePayload(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return undefined;
    }
  }

  return asRecord(value);
}

function readMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    const messages = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    if (messages.length > 0) return messages.join('; ');
  }
  return undefined;
}

/**
 * Normalizes the response shapes emitted by umi-request and by callers that
 * use `getResponse: true` so mutation forms can consistently handle errors.
 */
export function getApiErrorInfo(error: unknown): ApiErrorInfo {
  const root = asRecord(error);
  const response = asRecord(root?.response);
  const responseData = parsePayload(response?.data);
  const directData = parsePayload(root?.data);
  const nestedResponse = parsePayload(directData?.response);
  const payloads = [responseData, directData, nestedResponse, root].filter(
    (payload): payload is Record<string, unknown> => payload !== undefined
  );

  let message: string | undefined;
  let code: string | undefined;
  for (const payload of payloads) {
    message ??= readMessage(payload.message);
    code ??= readMessage(payload.code);

    const nestedError = parsePayload(payload.error);
    if (nestedError) {
      message ??= readMessage(nestedError.message);
      code ??= readMessage(nestedError.code);
    }
  }

  const statusCandidate = response?.status ?? root?.status ?? responseData?.statusCode ?? directData?.statusCode;
  const status = typeof statusCandidate === 'number' ? statusCandidate : undefined;
  const request = asRecord(root?.request);
  const requestOptions = asRecord(request?.options);
  const url =
    typeof response?.url === 'string'
      ? response.url
      : typeof request?.url === 'string'
      ? request.url
      : typeof requestOptions?.url === 'string'
      ? requestOptions.url
      : undefined;

  return { status, code, message, url };
}

export function isMfaError(error: unknown): boolean {
  const { message, code } = getApiErrorInfo(error);
  return MFA_ERROR_MESSAGES.has(message ?? '') || MFA_ERROR_MESSAGES.has(code ?? '');
}

export const MFA_ERROR_FIELD_MESSAGE = 'The MFA token is invalid or expired. Enter a new token and submit again.';
